import { debounce } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useRef, useState } from 'react'
import ButtonVariantEnum from '../../enums/ButtonVariant'
import { useClickOutside } from '../../hooks/useClickOutSide'
import RoutingEnum from '../../routes/appRoutes'
import ProductsList from '../ProductsList/ProductsList'
import {
  SearchIcon,
  StyledAutocompletion,
  StyledInput,
  StyledInputContainer,
  StyledMainContainer,
  StyledParagraph,
  StyledSearchBar,
  StyledSearchButton,
  StyledSearchForm,
  StyledSearchTop,
  StyledProductSelect,
} from './SearchBarProducts.styled'

const GET = 'GET'
const KEYWORDS_PLACEHOLDER = 'Search for a product'
const MARKETS_PLACEHOLDER = 'Select a market'
const MARKETS = 'markets'
const SEARCH_LABEL = 'Search'
const INPUT_NAME = 'keywords'
const INPUT_TYPE = 'text'
const SELECT_PREFIX = 'Markets :'

const SearchBarProducts = (props) => {
  const {
    className,
    handleAutocompletion,
    handleNewSearch,
    keywords,
    options,
    filters,
    productsList,
    setFilters,
    setKeywords,
    suggestions,
    text,
  } = props

  const isOurProducts = text ? true : false

  const [inputFocused, setInputFocused] = useState(false)
  const [resultsFocused, setResultsFocused] = useState(false)

  const launchAutocomplete = useCallback(
    debounce(handleAutocompletion, 300),
    []
  )

  const inputRef = useRef(null)
  useClickOutside(inputRef, () => setInputFocused(false))

  const formRef = useRef(null)
  useClickOutside(formRef, () => setResultsFocused(false))

  const handleOnSubmit = async (e) => {
    e.preventDefault()
    setInputFocused(false)
    await handleNewSearch()
    setResultsFocused(true)
  }

  const changeKeywords = (keywords) => {
    setKeywords(keywords)
    launchAutocomplete(keywords)
  }

  const handleOnChange = (event) => {
    changeKeywords(event.target.value)
  }

  const handleOnChangeAutocompletion = (suggest) => {
    changeKeywords(suggest)
  }

  const handleFilterChange = (e, identifier) => {
    const values =
      Array.isArray(e.select) && e.select.map((option) => option.value)
    setFilters({
      ...filters,
      [identifier]: values || e.select.value,
    })
  }

  return (
    <StyledSearchBar
      className={`${className} requestScrollTo`}
      isOurProducts={isOurProducts}
    >
      {text && <StyledParagraph>{text}</StyledParagraph>}
      <StyledMainContainer>
        <StyledSearchForm
          action={RoutingEnum.search}
          method={GET}
          onSubmit={handleOnSubmit}
          ref={formRef}
          role="search"
        >
          <StyledSearchTop>
            <StyledInputContainer
              inputFocused={inputFocused && suggestions.length}
              isOurProducts={isOurProducts}
              ref={inputRef}
            >
              <StyledInput
                aria-label={INPUT_NAME}
                autoComplete="off"
                isOurProducts={isOurProducts}
                onChange={handleOnChange}
                name={INPUT_NAME}
                onFocus={() => setInputFocused(true)}
                type={INPUT_TYPE}
                value={keywords}
                placeholder={KEYWORDS_PLACEHOLDER}
              />
              <SearchIcon />
              {inputFocused && suggestions.length ? (
                <StyledAutocompletion
                  suggestions={suggestions}
                  setKeywords={setKeywords}
                  handleOnChange={handleOnChangeAutocompletion}
                  smallSearchbar={true}
                />
              ) : null}
            </StyledInputContainer>
            <StyledProductSelect
              prefix={SELECT_PREFIX}
              placeholder={MARKETS_PLACEHOLDER}
              isOurProducts={isOurProducts}
              isMulti={true}
              onChange={(e) => handleFilterChange(e, MARKETS)}
              options={options[MARKETS]}
              value={filters[MARKETS].map((value) => ({
                value,
                label: value,
              }))}
            />
          </StyledSearchTop>
          <StyledSearchButton
            label={SEARCH_LABEL}
            variant={
              isOurProducts
                ? ButtonVariantEnum.primary
                : ButtonVariantEnum.secondary
            }
          />
          {resultsFocused && !inputFocused && (
            <ProductsList
              isOurProducts={isOurProducts}
              products={productsList}
            />
          )}
        </StyledSearchForm>
      </StyledMainContainer>
    </StyledSearchBar>
  )
}

SearchBarProducts.propTypes = {
  className: PropTypes.string,
  handleAutocompletion: PropTypes.func.isRequired,
  handleNewSearch: PropTypes.func.isRequired,
  keywords: PropTypes.string.isRequired,
  options: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  productsList: PropTypes.array,
  setFilters: PropTypes.func.isRequired,
  setKeywords: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.string),
  text: PropTypes.string,
}

export default SearchBarProducts
